/* LEGEND — floating BORROW / short-availability card. Ported from legend/borrow.jsx.
   Pre-flight before a short: can I borrow it, what does it cost, and how far must
   price fall before the trade clears borrow + spread? verdict hero → break-even
   bar → cost facts → locate inventory → short sizing across the five buckets.
   Draggable / collapsible.

   READ-ONLY. Nothing here places an order or spends a locate. The broker facts
   are swept nightly (symbols.borrow); every number on the card is recomputed
   from the LIVE quote on each tick via lib/borrow.ts. */
import React from 'react';
import { Icon } from './Widgets';
import { subscribeQuotes } from '../data/source';
import { deriveBorrowFrame, type BorrowFrame } from '../lib/borrow';

const { useRef: useRefB, useState: useStateB, useEffect: useEffectB, useMemo: useMemoB } = React;
const h = React.createElement;

// Precision is a property of the COLUMN, not of the value. Deciding per value
// put "$996.49" directly above "$1,993" and it read like a typo.
const usd = (v: number | null | undefined) =>          // exact money: fees, locate cost
  v == null ? '—' : (v < 0 ? '−$' : '$') + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usdWhole = (v: number | null | undefined) =>     // magnitudes: notional, margin, buying power
  v == null ? '—' : (v < 0 ? '−$' : '$') + Math.round(Math.abs(v)).toLocaleString('en-US');
const qty = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('en-US'));
const px = (v: number | null | undefined, p = 2) => (v == null ? '—' : v.toFixed(p));
const TONE: Record<string, string> = { ok: 'var(--up)', warn: 'var(--warn)', warn2: '#f5883b', bad: 'var(--down)' };

/** "quoted 6h ago" — the card must never imply the inventory number is live. */
function ageOf(iso: string | null | undefined): string {
  if (!iso) return 'age unknown';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'age unknown';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `quoted ${m}m ago`;
  const hrs = Math.floor(m / 60);
  return hrs < 48 ? `quoted ${hrs}h ago` : `quoted ${Math.floor(hrs / 24)}d ago`;
}

/** A locate's expiry in exchange time — the only clock that decides it. */
function etClock(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch { return '—'; }
}

function fact(k: string, v: any, sub: any, tone?: string | null) {
  return h('div', { className: 'brw-fact', key: k },
    h('span', { className: 'brwf-k' }, k),
    h('span', { className: 'brwf-v num' + (tone ? ' ' + tone : '') }, v),
    sub ? h('span', { className: 'brwf-s' }, sub) : null);
}

export function BorrowCard(props: any) {
  const { data, symbol, lastPrice, pos, collapsed, onMove, onToggle, onClose, docked } = props;
  const cardRef = useRefB<any>(null);
  const [nbbo, setNbbo] = useStateB<any>(null);

  // The card owns its quote subscription — the WS layer ref-counts channels, so
  // a third subscriber alongside ChartHeader/ChartPane costs nothing.
  useEffectB(() => {
    if (!symbol) return;
    setNbbo(null);
    const off = subscribeQuotes(symbol, (q: any) => setNbbo({ bid: q.bid ?? null, ask: q.ask ?? null }));
    return () => off();
  }, [symbol]);

  const frame: BorrowFrame | null = useMemoB(() => {
    if (!data?.borrow) return null;
    return deriveBorrowFrame({
      symbol,
      facts: data.borrow,
      daily: data.daily ?? { sessionLow: null, prevClose: null, priorDayLow: null, prevPrevClose: null, ssrTriggerRatio: 0.9 },
      account: data.account ?? { equity: null, buyingPower: null, regtBuyingPower: null, shortingEnabled: null },
      params: data.params ?? null,
      quote: { bid: nbbo?.bid ?? null, ask: nbbo?.ask ?? null, last: lastPrice || null },
      // Borrow already held sizes the short exactly as the worker sizes it.
      heldLocateQty: data?.locate ? Number(data.locate.remaining) || 0 : 0,
    });
  }, [data, symbol, lastPrice, nbbo]);

  function startDrag(e: any) {
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    const ox = (pos.x == null && cardRef.current) ? cardRef.current.offsetLeft : (pos.x || 0);
    const sx = e.clientX, sy = e.clientY, oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }

  if (!frame) return null;
  const f = frame, prec = f.prec, b = f.borrow, mk = f.market, sz = f.sizing, dec = f.decision, mg = f.margin, ssr = f.ssr;
  const tone = TONE[f.verdictTone] || 'var(--t2)';
  const blocked = f.state === 'NO_INVENTORY' || f.state === 'THRESHOLD_SECURITY' || f.state === 'UNKNOWN';
  const insufficient = f.state === 'INSUFFICIENT_INVENTORY';
  const etb = f.state === 'EASY_TO_BORROW';
  // Borrow we HOLD — bought, paid for, ours until it expires. The rest of this
  // card describes the DESK's inventory, and the two are routinely confused:
  // the desk can be empty while these shares sit ready to short (DCOY
  // 2026-09-22 — 300 held against a NO_INVENTORY sweep).
  const held = data?.locate && Number(data.locate.remaining) > 0 ? data.locate : null;

  const head = h('div', { className: 'pc-head brw-head', onPointerDown: docked ? undefined : startDrag },
    h('span', { className: 'pc-sym' }, symbol),
    h('span', { className: 'brw-tag' }, 'Borrow'),
    h('span', { className: 'brw-state', style: { '--tone': tone } as any }, h('i', { className: 'brw-state-dot' }), f.stateLabel),
    ssr.active
      ? h('span', { className: 'brw-ssr-badge', title: `Short-sale restriction — ${ssr.reason}. A short may only execute ABOVE the national best bid.` }, 'SSR')
      : null,
    h('div', { className: 'pc-actions' },
      h('button', { className: 'icon-btn sm', onClick: onToggle, title: collapsed ? 'Expand' : 'Collapse' }, Icon.chev()),
      h('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x())));

  if (collapsed) {
    return h('div', { ref: cardRef, className: 'pos-card brw-card collapsed', style: { left: pos.x + 'px', top: pos.y + 'px' } }, head);
  }

  // ---- verdict hero: what a lot costs ----
  // Locates come in lots of 100, so that is the unit quoted. No basis points
  // anywhere on this card — "$2.52 per 100 shares, needs $0.031 down" is a
  // thing you can hold in your head while watching the tape; "278 bps" is not.
  const pl = dec.perLot;
  const hero = h('div', { className: 'brw-hero', style: { '--tone': tone } as any },
    h('div', { className: 'brw-hero-l' },
      h('div', { className: 'brw-verdict' }, f.verdict),
      h('div', { className: 'brw-shortable' },
        h('i', { className: 'brw-sh-dot ' + (etb ? 'on' : blocked ? 'off' : 'warn') }),
        etb ? 'shortable now'
          : held ? `${qty(held.remaining)} sh held — shortable`
            : blocked ? 'not borrowable' : 'locate required')),
    h('div', { className: 'brw-hero-r' },
      h('div', { className: 'brw-cost-big' }, etb ? 'FREE' : pl.fee == null ? '—' : usd(pl.fee)),
      h('div', { className: 'brw-cost-k' }, etb ? 'no borrow fee' : pl.fee == null ? 'no quote' : `locate · per ${b.roundLot} sh`)));

  // The one sentence the card exists to say: enter here, clear costs below there.
  const heroLine = !blocked && dec.breakEvenPrice != null ? h('div', { className: 'brw-hero-line' },
    'Short at ', h('b', null, px(mk.bid, prec)), ' · needs ',
    h('b', null, '$' + px(dec.costPerShare, prec === 2 ? 3 : 4)), ' down · break even ',
    h('b', null, px(dec.breakEvenPrice, prec))) : null;

  // ---- cost per lot, split fee vs spread, in dollars ----
  const feeD = pl.fee ?? 0, spD = pl.spread ?? 0;
  const breakeven = !blocked ? h('div', { className: 'brw-be' },
    h('div', { className: 'brw-be-top' },
      h('span', { className: 'brw-be-k' }, `Cost per ${b.roundLot} shares`),
      h('span', { className: 'brw-be-v' }, usd(pl.total))),
    h('div', { className: 'brw-be-bar' },
      h('div', { className: 'brw-be-fee', style: { flex: Math.max(feeD, 0.01) }, title: `Locate ${usd(pl.fee)}` }, feeD ? usd(pl.fee) + ' locate' : null),
      h('div', { className: 'brw-be-sp', style: { flex: Math.max(spD, 0.01) }, title: `Spread ${usd(pl.spread)}` }, usd(pl.spread) + ' spread')),
    // A break-even only means something next to how far the symbol travels.
    // $0.03 to clear costs reads expensive until you see it covers $0.18 a day.
    h('div', { className: 'brw-be-cap' },
      dec.range != null && dec.rangeMultiple != null
        ? [`${dec.rangeIsToday ? "Today's" : "Yesterday's"} range `, h('b', { key: 'r' }, '$' + px(dec.range, prec)),
          ` — ${dec.rangeMultiple}\u00d7 the break-even.`]
        : 'No session range yet to size the break-even against.')) : null;

  // ---- cost facts, dollars only ----
  const facts = h('div', { className: 'brw-facts' },
    fact('Borrow fee', b.feePerShare == null ? (etb ? 'ETB' : '\u2014') : '$' + px(b.feePerShare, 4),
      b.feePerShare == null ? (etb ? 'no locate' : 'unquoted') : `per sh \u00b7 ${usd(pl.fee)} per ${b.roundLot}`),
    fact('Spread', mk.spread == null ? '\u2014' : '$' + px(mk.spread, 4),
      mk.bid == null || mk.ask == null ? 'awaiting quote' : px(mk.bid, prec) + ' \u00d7 ' + px(mk.ask, prec)),
    // Margin is what surprises on cheap stock: the $2.50/share floor means a
    // $0.90 name ties up ~2.8x its own notional.
    fact('Margin', '$' + mg.perShare + '/sh', `${usd(pl.margin)} per ${b.roundLot} \u00b7 ${mg.rule}`),
    fact('SSR', !ssr.known ? 'n/a' : ssr.active ? 'ACTIVE' : 'clear',
      !ssr.known ? 'no prior close' : ssr.active ? (ssr.reason || 'uptick-only') : px(ssr.triggerPrice, prec) + ' trig',
      ssr.active ? 'bad' : null));

  // ---- locate inventory ----
  const need = sz.presets.find((r) => r.key === 'full')?.shares || 0;
  const have = b.availableQty ?? 0;
  const availPct = blocked ? 0 : need > 0 ? Math.min(100, (have / need) * 100) : have > 0 ? 100 : 0;
  const avail = !etb ? h('div', { className: 'brw-avail' },
    h('div', { className: 'brw-avail-top' },
      h('span', { className: 'brw-avail-k' }, 'Locate inventory'),
      h('span', { className: 'brw-avail-v' }, b.availableQty == null ? (blocked ? 'none' : '—') : qty(b.availableQty) + ' sh')),
    h('div', { className: 'brw-avail-bar' + (insufficient ? ' short' : blocked ? ' none' : '') }, h('i', { style: { width: availPct + '%' } })),
    // Availability only ever FALLS during a session, so the swept number is an
    // upper bound. Showing its age is the difference between a fact and a guess.
    h('div', { className: 'brw-avail-cap' + (insufficient ? ' warn' : '') },
      insufficient
        ? ['Only ', h('b', { key: 'q' }, qty(b.availableQty) + ' sh'), ` locatable — under one ${b.roundLot}-share lot, so no short can be sized.`]
        : `Upper bound · ${ageOf(b.quotedAt)}`)) : null;

  // ---- what we HOLD (as distinct from what the desk has) ----
  // Rendered whenever a locate exists, including on a blocked state — that is
  // exactly the case the operator cannot otherwise see, and the one where the
  // rest of the card reads "no borrow" while 300 shares sit in the account.
  const heldBox = held ? h('div', { className: 'brw-held' },
    h('div', { className: 'brw-held-top' },
      h('span', { className: 'brw-held-k' }, h('i', { className: 'brw-held-dot' }), 'Borrow held'),
      h('span', { className: 'brw-held-v' }, qty(held.remaining) + ' sh')),
    h('div', { className: 'brw-held-row' },
      h('span', null, 'Located ', h('b', null, qty(held.qty))),
      h('span', null, 'Shorted ', h('b', null, qty(held.used || 0))),
      h('span', null, 'Expires ', h('b', null, etClock(held.expiresAt)), ' ET')),
    // The ceiling is cumulative and the broker exposes the remainder nowhere
    // but a 403 string (GPRO 2026-09-01) — so this sentence is the only warning
    // there is.
    h('div', { className: 'brw-held-cap' },
      (held.used > 0
        ? `${qty(held.used)} of ${qty(held.qty)} already shorted against this locate — covering does NOT return it. `
        : '') + 'Shorting draws this down permanently.')) : null;

  // ---- sizing across the five buckets ----
  const sizing = (!blocked || held) ? h('div', { className: 'brw-sizing' },
    h('div', { className: 'brw-sec-h' }, 'Short size',
      h('span', { className: 'brw-basis' }, 'binds on ', h('b', null, sz.binding || '—'))),
    h('div', { className: 'brw-sz-row brw-sz-head' },
      h('span', null, ''), h('span', null, 'SHARES'), h('span', null, 'NOTIONAL'), h('span', null, 'LOCATE $'), h('span', null, 'MARGIN')),
    sz.presets.map((r) => h('div', { className: 'brw-sz-row' + (r.shares <= 0 ? ' nil' : ''), key: r.key },
      h('span', { className: 'brw-sz-lab' }, r.name),
      h('span', { className: 'num', title: r.binding ? 'capped by ' + r.binding : undefined }, r.shares > 0 ? qty(r.shares) : '—'),
      h('span', { className: 'num dim' }, r.shares > 0 ? usdWhole(r.notional) : '—'),
      h('span', { className: 'num' + (r.locateCost ? ' warn' : '') }, r.locateCost == null ? (etb ? 'free' : '—') : usd(r.locateCost)),
      h('span', { className: 'num dim' }, r.shares > 0 ? usdWhole(r.margin) : '—'))),
    h('div', { className: 'brw-cap' },
      'Buying power ', h('b', null, usdWhole(f.account.buyingPower)),
      f.account.regtBuyingPower != null ? [' · overnight ', h('b', { key: 'o' }, usdWhole(f.account.regtBuyingPower))] : null,
      sz.needsLocate ? ` · sized to use every located share` : ' · no locate needed')) : null;

  const blockedBox = blocked ? h('div', { className: 'brw-blocked' },
    h('svg', { viewBox: '0 0 24 24', width: 15, height: 15, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
      h('circle', { cx: 12, cy: 12, r: 9 }), h('path', { d: 'M5.6 5.6l12.8 12.8' })),
    h('div', null,
      h('div', { className: 'brw-blocked-h' },
        f.state === 'THRESHOLD_SECURITY' ? 'On the Reg SHO threshold list'
          : f.state === 'UNKNOWN' ? 'No borrow quote' : 'No borrow inventory'),
      h('div', { className: 'brw-blocked-s' },
        held
          ? `The desk has none left to SELL — but ${qty(held.remaining)} shares are already located and held here, so a short up to that size still goes through until ${etClock(held.expiresAt)} ET. No more can be bought while this lasts.`
          : (b.errorMessage || 'No shares available to locate right now — a short cannot be opened.')))) : null;

  const foot = h('div', { className: 'brw-foot' },
    h('span', { className: 'brw-foot-hint' },
      dec.canShort ? 'Cost checks out — borrow is available at this size.'
        : f.account.shortingEnabled === false ? 'Account is not enabled for shorting.'
          : held ? `Nothing left on the desk — the ${qty(held.remaining)} shares held here are the size limit.`
            : 'Borrow blocks a short here.'),
    h('span', { className: 'brw-seq' }, h('i', { className: 'brw-live-dot' }), nbbo ? 'live' : 'awaiting quote'));

  return h('div', { ref: cardRef, className: 'pos-card brw-card', style: { left: pos.x + 'px', top: pos.y + 'px' } },
    head,
    h('div', { className: 'pc-body brw-body' }, hero, heroLine, blockedBox, heldBox, breakeven, facts, avail, sizing, foot));
}
